import { create } from 'zustand';
import type { OfferOrderDto, OfferOrderState, OfferUsageDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * What a coupon or an automatic offer did (ADR-063) — the figures over every
 * order that used it, and those orders, filtered on the server by what became
 * of them.
 */

export interface UsageSubject {
  kind: 'coupon' | 'offer';
  id: string;
  /** The code, or the offer's name — the panel's title. */
  name: string;
}

interface OfferUsageState {
  subject: UsageSubject | null;
  usage: OfferUsageDto | null;
  orders: OfferOrderDto[];
  cursor: string | null;
  filter: '' | OfferOrderState;
  loading: boolean;
  error: string | null;

  open: (subject: UsageSubject) => Promise<void>;
  filterBy: (filter: '' | OfferOrderState) => Promise<void>;
  loadMore: () => Promise<void>;
  close: () => void;
}

const PAGE_SIZE = 20;

function ordersOf(subject: UsageSubject, filter: '' | OfferOrderState, cursor?: string) {
  const query = { limit: PAGE_SIZE, ...(filter ? { state: filter } : {}), ...(cursor ? { cursor } : {}) };
  return subject.kind === 'coupon'
    ? adminApi.discounts.orders(subject.id, query)
    : adminApi.promotions.orders(subject.id, query);
}

export const useOfferUsageStore = create<OfferUsageState>((set, get) => ({
  subject: null,
  usage: null,
  orders: [],
  cursor: null,
  filter: '',
  loading: false,
  error: null,

  async open(subject) {
    set({ subject, usage: null, orders: [], cursor: null, filter: '', loading: true, error: null });
    try {
      const [usage, page] = await Promise.all([
        withSession(() => (subject.kind === 'coupon' ? adminApi.discounts.usage(subject.id) : adminApi.promotions.usage(subject.id))),
        withSession(() => ordersOf(subject, '')),
      ]);
      // A panel closed or switched meanwhile keeps its own answer.
      if (get().subject?.id !== subject.id) return;
      set({ usage, orders: page.items, cursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load what it did.') });
    }
  },

  async filterBy(filter) {
    const subject = get().subject;
    if (!subject) return;
    set({ filter, loading: true, error: null });
    try {
      const page = await withSession(() => ordersOf(subject, filter));
      set({ orders: page.items, cursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the orders.') });
    }
  },

  async loadMore() {
    const { subject, cursor, filter, loading, orders } = get();
    if (!subject || !cursor || loading) return;
    set({ loading: true });
    try {
      const page = await withSession(() => ordersOf(subject, filter, cursor));
      set({ orders: [...orders, ...page.items], cursor: page.meta.nextCursor, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load more.') });
    }
  },

  close() {
    set({ subject: null, usage: null, orders: [], cursor: null, filter: '', error: null });
  },
}));
